import { useCallback, useEffect, useMemo, useState } from 'react';

import dlv from 'dlv';

import { GetMeDocument, SetLoyaltyCardRewardsTierMutationHookResult } from 'generated/rbi-graphql';
import useDebouncedEffectOnUpdates from 'hooks/use-debounce-effect-on-updates';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useAuthContext } from 'state/auth';
import { NUM_RECENT_PURCHASED_ITEMS, UserDetails } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { useThLoyaltyContext } from 'state/loyalty';
import { ILoyaltyCategory, ILoyaltyTier } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import noop from 'utils/noop';

export const useBankMyRewardsTierId = () => {
  const { getLoyaltyTier } = useThLoyaltyContext();

  const [bankMyRewardTierId, setBankMyRewardTierId] = useState<string | null>(null);

  useEffect(() => {
    getLoyaltyTier().then((loyaltyTier: ILoyaltyTier) => {
      if (!loyaltyTier) {
        setBankMyRewardTierId(null);
        return;
      }

      setBankMyRewardTierId(loyaltyTier._id);
    });
  }, [getLoyaltyTier, setBankMyRewardTierId]);

  return { bankMyRewardTierId };
};

export type SelectedRewardsTierOptions = Partial<{
  onSetRewardsTierError: () => void;
  loyaltyTiers: ILoyaltyTier[];
}>;

export const useSelectedRewardTier = (
  loyaltyCategories: ILoyaltyCategory[] | null = [],
  setSelectedRewardTier: SetLoyaltyCardRewardsTierMutationHookResult[0],
  { loyaltyTiers, onSetRewardsTierError = noop }: SelectedRewardsTierOptions = {}
) => {
  const { user } = useAuthContext();
  const { logEvent } = useMParticleContext();
  const logger = useLogger();
  const shouldUniqueByModifiers = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS);

  const logRewardTierChangedEvent = useCallback(
    (rewardTierId: string, success: boolean, message?: string) => {
      logEvent(CustomEventNames.REWARD_TIER_CHANGED, EventTypes.UserPreference, {
        'Reward Tier ID': rewardTierId,
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
      });
    },
    [logEvent]
  );

  const loyaltyTier = dlv(user || {}, 'details.loyaltyTier');

  const [selectedRewardTierId, setSelectedRewardTierIdOnChange] = useState<
    UserDetails['details']['loyaltyTier']
  >(loyaltyTier);

  useEffectOnUpdates(() => {
    if (selectedRewardTierId !== loyaltyTier) {
      setSelectedRewardTierIdOnChange(loyaltyTier);
    }
  }, [loyaltyTier]);

  /**
   * This effect controls sending the `updateMe` mutation with the user's
   * updated reward tier.
   */
  useDebouncedEffectOnUpdates(
    () => {
      if (!selectedRewardTierId || selectedRewardTierId === loyaltyTier) {
        return;
      }

      setSelectedRewardTier({
        variables: {
          loyaltyTier: selectedRewardTierId,
        },
        refetchQueries: [
          {
            query: GetMeDocument,
            variables: {
              customInput: { shouldUniqueByModifiers },
              numUniquePurchasedItems: NUM_RECENT_PURCHASED_ITEMS,
            },
          },
        ],
        awaitRefetchQueries: true,
      })
        .then(() => {
          logRewardTierChangedEvent(selectedRewardTierId, true);
        })
        .catch(error => {
          logger.error({ error });
          logRewardTierChangedEvent(selectedRewardTierId, false, error);
          onSetRewardsTierError();
        });
    },
    1000,
    [selectedRewardTierId]
  );

  const onChangeRewardTier = useCallback(
    (loyaltyTierId: string | null) => {
      if (!loyaltyTierId) {
        return;
      }

      setSelectedRewardTierIdOnChange(loyaltyTierId);
    },
    [setSelectedRewardTierIdOnChange]
  );

  const selectedRewardCategoryId = useMemo(() => {
    // if we have loyaltyTiers this is an onboarding update.
    if (loyaltyCategories === null && loyaltyTiers && loyaltyTiers.length) {
      const selectedTier = loyaltyTiers.find(({ _id }) => _id === selectedRewardTierId);
      return selectedTier ? selectedTier._id : selectedTier;
    }
    if (loyaltyCategories && loyaltyCategories.length) {
      const selected = loyaltyCategories.find(({ options }) =>
        options.find(({ _id }) => _id === selectedRewardTierId)
      );
      return selected ? selected._id : selected;
    }
    return undefined;
  }, [loyaltyTiers, loyaltyCategories, selectedRewardTierId]);

  return {
    selectedRewardCategoryId,
    selectedRewardTierId,
    onChangeRewardTier,
  };
};
